import { isFunction } from 'lodash'
import { useCallback, useState } from 'react'

export type UsePersistentStateParams<T extends unknown> = {
  key: string
  defaultState: T | (() => T)
  serialize?: (value: T) => string
  deserialize?: (value: string) => T
  useSessionStorage?: boolean
  // TODO add versioning
}

function usePersistentState<T extends unknown = unknown>({
  key,
  defaultState,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
  useSessionStorage = false,
}: UsePersistentStateParams<T>) {
  const [persistentState, setPersistentState] = useState(() => {
    let value: T = null

    function getValue() {
      return isFunction(defaultState) ? defaultState() : defaultState
    }

    if (typeof window === 'undefined') {
      value = getValue()
    } else {
      try {
        const persistentValue = useSessionStorage ? sessionStorage.getItem(key) : localStorage.getItem(key)

        if (persistentValue) {
          value = deserialize(persistentValue)
        } else {
          value = getValue()
        }
      } catch {
        value = getValue()
      }
    }

    return value
  })

  const handleSetPersistentState = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      setPersistentState((prevValue) => {
        const nextValue = isFunction(value) ? value(prevValue) : value

        if (useSessionStorage) {
          sessionStorage.setItem(key, serialize(nextValue))
        } else {
          localStorage.setItem(key, serialize(nextValue))
        }

        return nextValue
      })
    },
    [key, serialize, useSessionStorage]
  )

  const handleRemovePersistentState = useCallback(() => {
    setPersistentState(null)
    localStorage.removeItem(key)
  }, [key])

  return [persistentState, handleSetPersistentState, handleRemovePersistentState] as const
}

export default usePersistentState
